import { useEffect, useRef, useState, useCallback } from 'react';
import type { FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Search, User, MapPin, Landmark, Globe, Loader2, X, Star } from 'lucide-react';
import { AdultCountModal, DatePickerModal } from '@/components/modals';
import { Modal } from '@/components/ui';
import type { SearchFilters } from '@mondivago/types';
import { buildCountryPath, buildCityPath, buildAttractionPath, buildTourPath } from '@/shared/seo/seoRouting';
import { getFlagEmoji, formatDateDisplay } from '@/shared/lib/formatters';
import { getSupabaseImageUrl } from '@/shared/lib/image';
import { useAutocomplete, type AutocompleteResult } from '@/hooks/contents';

interface SearchBarProps {
  onSearch?: (filters: SearchFilters) => void;
  defaultLocation?: string;
  defaultDate?: Date | null;
  defaultAdults?: number;
  defaultChildren?: number;
  autoSearchOnFilterChange?: boolean;
  variant?: 'hero' | 'navbar';
  disableAutocomplete?: boolean;
}

export function SearchBar({
  onSearch,
  defaultLocation = '',
  defaultDate,
  defaultAdults,
  defaultChildren,
  autoSearchOnFilterChange = false,
  variant = 'hero',
  disableAutocomplete = false,
}: SearchBarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [location, setLocation] = useState(defaultLocation);
  const { results: autocompleteResults, isLoading: isAutocompleteLoading } = useAutocomplete(
    disableAutocomplete ? '' : location
  );
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [selectedSuggestionIdx, setSelectedSuggestionIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileAutocompleteRef = useRef<HTMLDivElement>(null);
  const desktopAutocompleteRef = useRef<HTMLDivElement>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (defaultDate !== undefined) return defaultDate;
    return null;
  });
  const [adults, setAdults] = useState(defaultAdults ?? 1);
  const [childrenCount, setChildrenCount] = useState(defaultChildren ?? 0);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAdultModalOpen, setIsAdultModalOpen] = useState(false);
  const dateButtonRef = useRef<HTMLButtonElement>(null);
  const adultButtonRef = useRef<HTMLButtonElement>(null);

  const [prevProps, setPrevProps] = useState({
    defaultLocation,
    defaultDate,
    defaultAdults,
    defaultChildren,
  });

  if (
    defaultLocation !== prevProps.defaultLocation ||
    defaultDate !== prevProps.defaultDate ||
    defaultAdults !== prevProps.defaultAdults ||
    defaultChildren !== prevProps.defaultChildren
  ) {
    setPrevProps({ defaultLocation, defaultDate, defaultAdults, defaultChildren });
    setLocation(defaultLocation);
    if (defaultDate !== undefined) setSelectedDate(defaultDate);
    if (defaultAdults !== undefined) setAdults(defaultAdults);
    if (defaultChildren !== undefined) setChildrenCount(defaultChildren);
  }

  const emitSearch = (overrides: Partial<SearchFilters> = {}, force = false) => {
    if (!autoSearchOnFilterChange && !force) return;

    const filters = {
      location: overrides.location ?? location,
      date: overrides.date ?? selectedDate,
      adults: overrides.adults ?? adults,
      children: overrides.children ?? childrenCount,
    };

    if (onSearch) {
      onSearch(filters);
      return;
    }

    const params = new URLSearchParams();
    if (filters.location.trim()) params.append('q', filters.location.trim());
    if (filters.date) params.append('date', filters.date.toISOString());
    if (filters.adults) params.append('adults', filters.adults.toString());
    if (filters.children) params.append('children', filters.children.toString());

    void navigate(`/search?${params.toString()}`);
  };

  // ─── Autocomplete search (debounced) ───
  useEffect(() => {
    if (location.trim().length >= 2) {
      if (autocompleteResults.length > 0) {
        // Only auto-open if the user is actively focused on the input
        if (document.activeElement === inputRef.current) {
          setIsAutocompleteOpen(true);
          setSelectedSuggestionIdx(-1);
        }
      } else {
        setIsAutocompleteOpen(false);
      }
    } else {
      setIsAutocompleteOpen(false);
      setSelectedSuggestionIdx(-1);
    }
  }, [location, autocompleteResults]);

  // Calculate portal position once on open (no laggy scroll/resize listeners)
  const formRef = useRef<HTMLFormElement>(null);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!isAutocompleteOpen || !formRef.current) {
      setPortalPos(null);
      return;
    }
    const rect = formRef.current.getBoundingClientRect();
    if (!rect) return;
    setPortalPos({
      top: rect.bottom + 8, // slight offset for the dropdown
      left: rect.left,
      width: rect.width,
    });
  }, [isAutocompleteOpen]);

  // Close dropdown on click outside (checks both mobile & desktop refs)
  useEffect(() => {
    if (!isAutocompleteOpen) return;
    const handler = (e: MouseEvent) => {
      const isInsideMobile = mobileAutocompleteRef.current?.contains(e.target as Node);
      const isInsideDesktop = desktopAutocompleteRef.current?.contains(e.target as Node);
      const isInsideInput = inputRef.current?.contains(e.target as Node);
      if (!isInsideMobile && !isInsideDesktop && !isInsideInput) {
        setIsAutocompleteOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [isAutocompleteOpen]);

  const handleSelectSuggestion = useCallback(
    (result: AutocompleteResult) => {
      setLocation(result.name);
      setIsAutocompleteOpen(false);
      setSelectedSuggestionIdx(-1);

      let path = '';
      switch (result.type) {
        case 'country':
          path = buildCountryPath(result.slug);
          break;
        case 'city':
          path = buildCityPath(result.countrySlug ?? '', result.slug);
          break;
        case 'attraction':
          path = buildAttractionPath(result.countrySlug ?? '', result.citySlug ?? '', result.slug);
          break;
        case 'tour':
          path = buildTourPath(result.countrySlug ?? '', result.citySlug ?? '', result.slug);
          break;
      }
      if (path) {
        void navigate(path);
      }
    },
    [navigate]
  );

  // Shared dropdown content
  const autocompleteDropdownContent = isAutocompleteLoading ? (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  ) : (
    <div className="py-2">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <div>
          <h3 className="nav-label text-gray-900">{t('home.suggestions', 'Suggestions')}</h3>
          <p className="mt-0.5 desc-sm !text-gray-400">{t('common.popular', 'Popular destinations and activities')}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setIsAutocompleteOpen(false);
          }}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-2 py-2 overflow-y-auto max-h-[380px]">
        {autocompleteResults.map((result, idx) => (
          <button
            key={`${result.type}-${result.id}`}
            type="button"
            onClick={() => {
              handleSelectSuggestion(result);
            }}
            onMouseEnter={() => {
              setSelectedSuggestionIdx(idx);
            }}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all ${
              idx === selectedSuggestionIdx ? 'bg-slate-50 ring-1 ring-slate-200 shadow-sm' : 'hover:bg-gray-50'
            }`}
          >
            {/* Left thumbnail image */}
            <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-gray-100">
              {result.imageUrl ? (
                <img
                  src={getSupabaseImageUrl(result.imageUrl, 'public', { width: 96, height: 96, format: 'webp' })}
                  alt={result.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-gray-400">
                  {(result.type === 'country' || result.type === 'city') && result.iso2 ? (
                    <span className="text-xl leading-none">{getFlagEmoji(result.iso2)}</span>
                  ) : (
                    <>
                      {result.type === 'country' && <Globe className="h-5 w-5" />}
                      {result.type === 'city' && <MapPin className="h-5 w-5" />}
                      {result.type === 'attraction' && <Landmark className="h-5 w-5" />}
                      {result.type === 'tour' && <Star className="h-5 w-5" />}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="nav-label text-gray-900 truncate">{result.name}</span>
                <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 meta-upper !text-[8px] text-gray-500">
                  {result.type === 'country' && t('common.discover', 'Country')}
                  {result.type === 'city' && t('common.city', 'City')}
                  {result.type === 'attraction' && t('common.attraction', 'Attraction')}
                  {result.type === 'tour' && t('common.activity', 'Activity')}
                </span>
              </div>
              <p className="desc-sm !text-gray-400 truncate mt-0.5">
                {result.type === 'country' && t('footer.links.explore', 'Explore country')}
                {result.type === 'city' && (
                  <span className="inline-flex items-center gap-1">
                    {result.iso2 && <span className="text-sm leading-none">{getFlagEmoji(result.iso2)}</span>}
                    <span className="truncate">{result.countryName ?? ''}</span>
                  </span>
                )}
                {(result.type === 'attraction' || result.type === 'tour') &&
                  [result.cityName, result.countryName].filter(Boolean).join(', ')}
              </p>
            </div>

            {result.type === 'tour' && result.startingPrice !== undefined && (
              <div className="shrink-0 flex flex-col items-end justify-center pl-2">
                <span className="text-xs font-black text-gray-900">
                  {result.startingPrice > 0
                    ? `From ${String(result.startingPrice)} ${result.currency ?? ''}`
                    : t('common.free', 'Free')}
                </span>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isAutocompleteOpen || autocompleteResults.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIdx((prev) => Math.min(prev + 1, autocompleteResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIdx((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && selectedSuggestionIdx >= 0) {
        e.preventDefault();
        handleSelectSuggestion(autocompleteResults[selectedSuggestionIdx]);
      } else if (e.key === 'Escape') {
        setIsAutocompleteOpen(false);
        setSelectedSuggestionIdx(-1);
      }
    },
    [isAutocompleteOpen, autocompleteResults, selectedSuggestionIdx, handleSelectSuggestion]
  );

  const handleSearch = () => {
    emitSearch({}, true);
  };

  const handleSelectDate = (date: Date | null) => {
    setSelectedDate(date);
    emitSearch({ date });
  };

  const handleSelectAdults = (count: number) => {
    setAdults(count);
    emitSearch({ adults: count });
    // Keep modal open to allow adding children or more adults
  };

  const handleSelectChildren = (count: number) => {
    setChildrenCount(count);
    emitSearch({ children: count });
  };

  const formatDate = (date: Date | null) => {
    if (!date) return t('common.flexible');
    return formatDateDisplay(date, {
      month: 'short',
      day: 'numeric',
    });
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    handleSearch();
  };

  return (
    <>
      <form
        ref={formRef}
        onSubmit={submitSearch}
        className={
          variant === 'navbar'
            ? 'relative z-30 flex w-full flex-col gap-1 rounded-full border border-gray-200 bg-white p-1.5 transition-shadow md:flex-row md:items-center'
            : 'relative z-30 flex w-full flex-col md:flex-row items-center border border-gray-200 rounded-[32px] md:rounded-full bg-white md:p-2 '
        }
      >
        {/* 1. Location Input */}
        <div className={`relative w-full ${variant === 'navbar' ? 'md:flex-1' : 'md:flex-[1.5] group'}`}>
          <div
            className={`w-full rounded-full transition-colors ${variant === 'navbar' ? 'px-4 py-2 hover:bg-gray-100' : 'px-6 py-4 md:py-3 cursor-text md:group-hover:bg-gray-100'}`}
            onClick={() => inputRef.current?.focus()}
          >
            {variant !== 'navbar' && (
              <label className="block eyebrow text-gray-800">{t('common.where', 'Where')}</label>
            )}
            <input
              ref={inputRef}
              type="text"
              placeholder={t('home.searchPlaceholder')}
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
              }}
              onFocus={() => {
                if (autocompleteResults.length > 0) setIsAutocompleteOpen(true);
              }}
              onKeyDown={handleKeyDown}
              className={`w-full bg-transparent outline-none truncate ${
                variant === 'navbar'
                  ? 'text-sm font-semibold text-gray-900 placeholder:text-gray-500'
                  : 'text-base font-semibold text-gray-900 placeholder:text-gray-400 mt-0.5'
              }`}
            />
          </div>

          {/* Mobile: inline dropdown */}
          {isAutocompleteOpen && (
            <div className="md:hidden">
              <div
                ref={mobileAutocompleteRef}
                className="relative mx-2 mt-2 mb-2 animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/[0.06]"
              >
                {autocompleteDropdownContent}
              </div>
            </div>
          )}
        </div>

        {/* Desktop Divider for Navbar Mode */}
        {variant === 'navbar' && <div className="hidden h-8 w-px bg-gray-200 md:block" />}

        <div
          className={`flex w-full md:w-auto items-center ${variant === 'navbar' ? 'gap-1' : 'gap-0 divide-x divide-gray-200 md:divide-none flex-1'}`}
        >
          {/* 2. Dates */}
          <div className={`relative flex-1 ${variant === 'navbar' ? 'md:flex-1' : 'group md:flex-1'}`}>
            <button
              type="button"
              ref={dateButtonRef}
              onClick={() => {
                setIsDatePickerOpen(true);
              }}
              className={`flex w-full flex-col items-start justify-center rounded-full transition-colors ${
                variant === 'navbar' ? 'px-4 py-2 hover:bg-gray-100' : 'px-6 py-4 md:py-3 md:group-hover:bg-gray-100'
              }`}
            >
              {variant !== 'navbar' && (
                <label className="block eyebrow text-gray-800 pointer-events-none">{t('common.when', 'When')}</label>
              )}
              <div
                className={`flex items-center gap-2 ${variant === 'navbar' ? 'text-sm font-semibold text-gray-900' : 'text-base font-semibold text-gray-600 mt-0.5'}`}
              >
                {variant === 'navbar' && <CalendarDays className="h-4 w-4 shrink-0 text-gray-400" />}
                <span className="truncate">{formatDate(selectedDate)}</span>
              </div>
            </button>

            <Modal
              isOpen={isDatePickerOpen}
              onClose={() => {
                setIsDatePickerOpen(false);
              }}
              anchorRef={dateButtonRef}
              title={t('common.selectDate')}
            >
              <div className="search-popover-content">
                <DatePickerModal
                  isOpen={true}
                  onClose={() => {
                    setIsDatePickerOpen(false);
                  }}
                  onSelectDate={handleSelectDate}
                  selectedDate={selectedDate}
                  asInline={true}
                />
              </div>
            </Modal>
          </div>

          {/* Desktop Divider for Navbar Mode */}
          {variant === 'navbar' && <div className="hidden h-8 w-px bg-gray-200 md:block" />}

          {/* 3. Participants */}
          <div className={`relative flex-1 ${variant === 'navbar' ? 'md:flex-[0.8]' : 'group md:flex-1'}`}>
            <div
              className={`flex w-full items-center justify-between rounded-full transition-colors ${
                variant === 'navbar'
                  ? 'px-4 py-2 hover:bg-gray-100'
                  : 'pl-6 pr-2 py-3 md:py-2 md:group-hover:bg-gray-100'
              }`}
            >
              <button
                type="button"
                ref={adultButtonRef}
                onClick={() => {
                  setIsAdultModalOpen(true);
                }}
                className="flex flex-col items-start flex-1 text-left"
              >
                {variant !== 'navbar' && (
                  <label className="block eyebrow text-gray-800 pointer-events-none">{t('common.who', 'Who')}</label>
                )}
                <div
                  className={`flex items-center gap-2 ${variant === 'navbar' ? 'text-sm font-semibold text-gray-900' : 'text-base font-semibold text-gray-600 mt-0.5'}`}
                >
                  {variant === 'navbar' && <User className="h-4 w-4 shrink-0 text-gray-400" />}
                  <span className="whitespace-nowrap">
                    {variant === 'navbar'
                      ? adults + childrenCount
                      : t('common.guestsCount', { count: adults + childrenCount })}
                  </span>
                </div>
              </button>

              {/* Search Button (Now outside the Who button for Hero) */}
              {variant !== 'navbar' && (
                <button
                  type="submit"
                  className="flex h-11 w-11 md:h-14 md:w-14 shrink-0 items-center justify-center rounded-full bg-lime-800 text-lime-500 hover:bg-lime-900 duration-300 transition-colors shadow-md ml-2 md:ml-4"
                  aria-label={t('common.search', 'Search')}
                >
                  <Search className="h-7 w-7" />
                </button>
              )}
            </div>

            <Modal
              isOpen={isAdultModalOpen}
              onClose={() => {
                setIsAdultModalOpen(false);
              }}
              anchorRef={adultButtonRef}
              title={t('common.participants')}
            >
              <AdultCountModal
                isOpen={true}
                onClose={() => {
                  setIsAdultModalOpen(false);
                }}
                onSelectCount={handleSelectAdults}
                selectedCount={adults}
                enableChildren={true}
                selectedChildrenCount={childrenCount}
                onSelectChildrenCount={handleSelectChildren}
                asInline={true}
              />
            </Modal>
          </div>

          {/* Search Button (Navbar); for Hero this is a hidden fallback so Enter still submits the form */}
          <button
            type="submit"
            aria-label={t('common.search', 'Search')}
            className={
              variant === 'navbar'
                ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lime-800 text-lime-400 transition-colors hover:bg-lime-900'
                : 'hidden'
            }
          >
            <Search className="h-5 w-5" />
            {variant !== 'navbar' && <span className="ml-2">{t('common.search', 'Search')}</span>}
          </button>
        </div>
      </form>

      {/* Desktop: portal-based dropdown (escapes hero overflow-hidden) */}
      {isAutocompleteOpen &&
        portalPos &&
        createPortal(
          <div
            ref={desktopAutocompleteRef}
            className="hidden md:block fixed z-[900] animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/[0.06]"
            style={{
              top: portalPos.top,
              left: portalPos.left,
              width: Math.max(portalPos.width, 320),
            }}
          >
            {autocompleteDropdownContent}
          </div>,
          document.body
        )}
    </>
  );
}
